import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import Icon from 'src/@core/components/icon';
import { useAFOPUTablaValorHistorico } from '../hooks/useAFOPUTablaValorHistorico';
import { OperacionAFOPUTablaValorHistoricoFields } from '../types/fields';
import { OperacionAFOPUTablaValorHistoricoGrupo } from '../types/dto';
import { pivotAFOPUData } from '../hooks/useTablaValorAFOPU';
import { error } from 'console';
import AFOPUTablaValores from './AFOPUTablaValores';

interface AFOPUTablaValoresHistorialDialogProps {
  idOperacion: number;
  open: boolean;
  onClose: () => void;

}

const VersionHistoricoItem = ({ version, expanded, onChange, loading, error }: {
  version: OperacionAFOPUTablaValorHistoricoGrupo,
  expanded: boolean,
  onChange: (isExp: boolean) => void,
  loading: boolean;
  error: any;
}) => {

  const { headers, rows } = useMemo(() => {
    const celdas = version[OperacionAFOPUTablaValorHistoricoFields.Celdas] || [];

    return pivotAFOPUData(celdas);
  }, [version]);

  const fecha = version[OperacionAFOPUTablaValorHistoricoFields.FechaModificacion];
  const usuario = version[OperacionAFOPUTablaValorHistoricoFields.NombreUsuarioModificacion];
  const obs = version[OperacionAFOPUTablaValorHistoricoFields.Observaciones];

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => onChange(isExpanded)}>
      <AccordionSummary expandIcon={<Icon icon='mdi:chevron-down' />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 4, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {fecha.toLocaleString()}
            </Typography>
            <Chip label={usuario} size='small' variant='outlined' color='primary' />
          </Box>
          {obs && (
            <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', maxWidth: '40%' }} noWrap>
              {obs}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {obs && (
          <Box sx={{ mb: 4, p: 3, bgcolor: 'action.hover', borderRadius: 1, borderLeft: '4px solid #ccc' }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>Observaciones del cambio:</Typography>
            <Typography variant='body2'>{obs}</Typography>
          </Box>
        )}
        <AFOPUTablaValores headers={headers} rows={rows} loading={loading} error={error} />
      </AccordionDetails>
    </Accordion>
  );
};

export const AFOPUTablaValoresHistoricoDialog = ({ idOperacion, open, onClose }: AFOPUTablaValoresHistorialDialogProps) => {
  const { versiones, isLoading, error } = useAFOPUTablaValorHistorico(idOperacion);
  const [expandedVersion, setExpandedVersion] = useState<string | false>(false);
  const [hasInitialized, setHasInitialized] = useState(false); // para que permita cerrar el primer accordion, sino al rerenderear se vuelve a abrir solo

  useEffect(() => {
    if (versiones.length > 0 && !hasInitialized) {
      const primeraFecha = versiones[0][OperacionAFOPUTablaValorHistoricoFields.FechaModificacion].toISOString();
      setExpandedVersion(primeraFecha);
      setHasInitialized(true);
    }
  }, [versiones, hasInitialized]);

  useEffect(() => {
    if (!open) {
      setHasInitialized(false);
      setExpandedVersion(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Icon icon="mdi:history" />
        Historial de Tabla de Valores - AFOPU
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
        ) : versiones.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No hay registros históricos.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {versiones.map((v) => {
              const id = v[OperacionAFOPUTablaValorHistoricoFields.FechaModificacion].toISOString();

              return (
                <VersionHistoricoItem
                  key={id}
                  version={v}
                  expanded={expandedVersion === id}
                  onChange={(isExpanded) => setExpandedVersion(isExpanded ? id : false)}
                  loading={isLoading}
                  error={error}
                />
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};